import { type VifRulesFamilyViewProps, VifRulesShell } from './VifRulesShell';
import { addSubVariantConfig } from './core/rulesAddSubConfig';
import { RULES_ADD_SUB_COPY } from './core/rulesAddSubCopy';
import type { RulesAddSubFlag } from './core/rulesAddSubProblems';
import { RULES_GRID } from './core/rulesVariantConfig';
import { useAddSubGame } from './hooks/useAddSubGame';
import { useVifRulesModule } from './hooks/useVifRulesModule';

export interface VifAddSubRulesViewProps extends VifRulesFamilyViewProps {
  flag: RulesAddSubFlag;
}

/**
 * DOM implementation of the 3x3 VIF addition rules board.
 *
 * Replaces the CreateJS `VIFRulesAddSub` render path for gameFlags 0-5. Game
 * rules, problem selection and pacing are ports of the shipping engine, not
 * reinterpretations - see `core/boardReducer.ts`,
 * `core/rulesAddSubProblems.ts` and `useRulesBoardGame.ts`.
 *
 * Everything this shares with the mul/div board - reading and posting the
 * player's position, running the game, the board, time on task, and the whole
 * screen - lives in `useVifRulesModule` and `VifRulesShell`. What is left here
 * is add/sub's own config, copy and problem selection.
 *
 * Round and game are owned by the server, not this component - see
 * `useVifProgress`, which reads the position, posts each completed game, and
 * re-reads it. Counting locally is not an option: the update endpoint rejects
 * any round/game that is not the one it last handed out, and the `GameRecord`
 * those writes produce is what stops `UpdateVIFStage` rolling the player's
 * stage backwards.
 */
export function VifAddSubRulesView({
  hubUrl,
  ...options
}: VifAddSubRulesViewProps) {
  const rules = useVifRulesModule({
    ...options,
    grid: RULES_GRID,
    resolveConfig: addSubVariantConfig,
    useGame: useAddSubGame,
  });

  return (
    <VifRulesShell
      rules={rules}
      copy={RULES_ADD_SUB_COPY[options.flag]}
      hubUrl={hubUrl}
      scoreboardShowsLayer
    />
  );
}
